import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Badge, Card, CardBody, CardHeader } from "reactstrap";

import type { DashboardPendingItem } from "../../../helpers/dashboardDayMetrics";
import { appointmentEstimatedCents } from "../../../helpers/dashboardDayMetrics";
import { formatMoneyFromCents } from "../../../helpers/money";

type Props = {
  items: DashboardPendingItem[];
  loading?: boolean;
  onOpenHealthRecord: (appointmentId: number) => void;
};

const relativeLabel = (
  days: number,
  t: (key: string, options?: Record<string, unknown>) => string
): string => {
  if (days <= 0) {
    return t("studio.dashboard.pendings.relativeToday");
  }
  if (days === 1) {
    return t("studio.dashboard.pendings.relativeDay");
  }
  return t("studio.dashboard.pendings.relativeDays", { count: days });
};

const DashboardPendingsCard = ({
  items,
  loading = false,
  onOpenHealthRecord,
}: Props) => {
  const { t, i18n } = useTranslation();

  return (
    <Card className="border-0 shadow-sm h-100 mb-0 rounded-3">
      <CardHeader className="bg-transparent border-0 pb-0 d-flex align-items-center justify-content-between">
        <h5 className="mb-0 fw-semibold">{t("studio.dashboard.pendings.title")}</h5>
        <Link to="/agenda" className="small fw-medium text-success text-decoration-none">
          {t("studio.dashboard.pendings.viewAll")}
        </Link>
      </CardHeader>
      <CardBody className="pt-2">
        {loading ? (
          <p className="text-muted mb-0">{t("studio.dashboard.loading")}</p>
        ) : items.length === 0 ? (
          <p className="text-muted mb-0">{t("studio.dashboard.pendings.empty")}</p>
        ) : (
          <ul className="list-unstyled mb-0">
            {items.map(({ appointment, relativeDays }) => {
              const cents = appointmentEstimatedCents(appointment);
              return (
                <li
                  key={appointment.id}
                  className="d-flex flex-wrap align-items-center justify-content-between gap-2 py-2 border-bottom border-light"
                >
                  <div className="d-flex flex-wrap align-items-center gap-2 min-w-0">
                    <span className="text-muted small text-nowrap">
                      {relativeLabel(relativeDays, t)}
                    </span>
                    <button
                      type="button"
                      className="btn btn-link p-0 text-start text-body text-decoration-none fw-medium text-truncate"
                      onClick={() => onOpenHealthRecord(appointment.id)}
                    >
                      #{appointment.id} ·{" "}
                      {appointment.client?.name ??
                        t("studio.dashboard.pendings.clientFallback")}
                    </button>
                    <Badge color="danger" pill className="bg-danger-subtle text-danger">
                      {t("studio.dashboard.pendings.noHealthRecord")}
                    </Badge>
                  </div>
                  <span className="fw-medium text-nowrap">
                    {formatMoneyFromCents(cents, i18n.language)}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </CardBody>
    </Card>
  );
};

export default DashboardPendingsCard;
